/*
  Stage 1 web components. No library, no build step. Each element is
  self-contained; storage goes only through window.DemoStore (demo-store.js).
*/

// In milliseconds. Shopify serves the CSS minified, so "400ms" arrives as ".4s":
// read without its unit it was 0.4 ms, and drawers closed with no slide (stage א12).
const cssDuration = (name, fallback) => {
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  const value = parseFloat(raw);
  if (!Number.isFinite(value)) return fallback;
  return /ms$/i.test(raw) ? value : /s$/i.test(raw) ? value * 1000 : value;
};
const cssValue = (name, fallback) =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
const desktopHover = window.matchMedia('(hover: hover) and (min-width: 860px)');
// "Reduce motion" (full pass, stage א7): scrolling the site does for the visitor jumps instead of gliding.
const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
window.AmorMotion = { scroll: () => (reduceMotionQuery.matches ? 'auto' : 'smooth') };

/* ---------------------------------------------------------------------------
   <amor-drawer>: the one drawer. A <dialog class="drawer"> inside.
   Opened by any element with data-drawer-open="<drawer id>".
--------------------------------------------------------------------------- */
class AmorDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    });
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-drawer-close]')) this.close();
    });
  }

  get isOpen() {
    return this.dialog.open;
  }

  open(trigger) {
    if (this.dialog.open) return;
    this.trigger = trigger || document.activeElement;
    this.dialog.showModal();
    document.documentElement.classList.add('is-locked');
    this.trigger?.setAttribute?.('aria-expanded', 'true');
    requestAnimationFrame(() => requestAnimationFrame(() => this.dialog.classList.add('is-open')));
    this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
  }

  close() {
    if (!this.dialog.open || this.closing) return;
    this.closing = true;
    this.dialog.classList.remove('is-open');
    window.setTimeout(() => {
      this.dialog.close();
      this.closing = false;
      if (!document.querySelector('dialog.drawer[open]')) {
        document.documentElement.classList.remove('is-locked');
      }
      this.trigger?.setAttribute?.('aria-expanded', 'false');
      this.trigger?.focus?.({ preventScroll: true });
      this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    }, cssDuration('--duration-drawer', 400));
  }
}
customElements.define('amor-drawer', AmorDrawer);

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-drawer-open]');
  if (!trigger) return;
  const drawer = document.getElementById(trigger.dataset.drawerOpen);
  if (!drawer || typeof drawer.open !== 'function') return;
  event.preventDefault();
  drawer.open(trigger);
});

/* ---------------------------------------------------------------------------
   <amor-menu-drawer>: the menu. Women/Men switch that remembers the last
   world, three tabs, and (stage א7, note 11.5) categories that open in place
   as <details> with a "+". The search row in the bottom rows closes the menu
   and opens the search panel (note 11.1).
--------------------------------------------------------------------------- */
class AmorMenuDrawer extends AmorDrawer {
  connectedCallback() {
    super.connectedCallback();
    this.switches = [...this.querySelectorAll('[data-world-switch]')];
    this.switches.forEach((button) =>
      button.addEventListener('click', () => {
        this.showWorld(button.dataset.worldSwitch);
        window.DemoStore?.setLastWorld(button.dataset.worldSwitch);
      })
    );
    this.addEventListener('click', (event) => {
      if (!event.target.closest('[data-menu-search]')) return;
      // The search panel opens once the menu has closed and handed focus back.
      this.addEventListener(
        'drawer:close',
        () => document.querySelector('amor-search')?.open(!window.matchMedia('(min-width: 860px)').matches),
        { once: true }
      );
      this.close();
    });

    // Three tabs (update 2, chapter 2): BY YOU opens by default.
    this.tabs = [...this.querySelectorAll('[data-menu-tab]')];
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.showTab(tab.dataset.menuTab));
      tab.addEventListener('keydown', (event) => {
        const step = { ArrowRight: 1, ArrowLeft: -1, Home: -index, End: this.tabs.length - 1 - index }[event.key];
        if (step === undefined) return;
        event.preventDefault();
        const next = this.tabs[(index + step + this.tabs.length) % this.tabs.length];
        this.showTab(next.dataset.menuTab);
        next.focus();
      });
    });
  }

  open(trigger) {
    const world = this.dataset.activeWorld;
    this.showTab('you');
    this.showWorld(world === 'women' || world === 'men' ? world : window.DemoStore?.lastWorld() || 'women');
    super.open(trigger);
  }

  showTab(name) {
    this.tabs.forEach((tab) => {
      const selected = tab.dataset.menuTab === name;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    });
    this.querySelectorAll('[data-menu-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.menuPanel !== name;
    });
  }

  close() {
    super.close();
    // The next opening starts with every category closed.
    window.setTimeout(
      () => this.querySelectorAll('.menu-list__group[open]').forEach((group) => group.removeAttribute('open')),
      cssDuration('--duration-drawer', 400)
    );
  }

  showWorld(world) {
    this.switches.forEach((button) =>
      button.setAttribute('aria-pressed', String(button.dataset.worldSwitch === world))
    );
    this.querySelectorAll('[data-world-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.worldPanel !== world;
    });
  }
}
customElements.define('amor-menu-drawer', AmorMenuDrawer);

/* ---------------------------------------------------------------------------
   <amor-accordion>: <details> items that open by height; "exclusive" keeps
   only one open (the mobile footer).
--------------------------------------------------------------------------- */
class AmorAccordion extends HTMLElement {
  connectedCallback() {
    this.items = [...this.querySelectorAll(':scope > details')];
    this.items.forEach((details) =>
      details.querySelector(':scope > summary').addEventListener('click', (event) => {
        event.preventDefault();
        this.toggle(details);
      })
    );
  }

  toggle(details) {
    if (details.open) {
      this.collapse(details);
      return;
    }
    if (this.hasAttribute('exclusive')) {
      this.items.filter((item) => item !== details && item.open).forEach((item) => this.collapse(item));
    }
    this.expand(details);
  }

  animate(content, from, to) {
    return content.animate([{ height: `${from}px` }, { height: `${to}px` }], {
      duration: cssDuration('--duration-accordion', 300),
      easing: cssValue('--ease', 'ease-out'),
    });
  }

  expand(details) {
    details.open = true;
    const content = details.querySelector('.accordion__content');
    if (content) this.animate(content, 0, content.scrollHeight);
  }

  collapse(details) {
    const content = details.querySelector('.accordion__content');
    if (!content) {
      details.open = false;
      return;
    }
    const animation = this.animate(content, content.scrollHeight, 0);
    let closed = false;
    const finish = () => {
      if (closed) return;
      closed = true;
      details.open = false;
      animation.cancel();
    };
    animation.onfinish = finish;
    // Animations pause in background tabs; the state must not wait for them.
    window.setTimeout(finish, cssDuration('--duration-accordion', 300) + 50);
  }
}
customElements.define('amor-accordion', AmorAccordion);

/* ---------------------------------------------------------------------------
   <amor-carousel>: a row that scrolls sideways (swipe on the phone). On
   desktop the arrows move it by one visible width and turn off at the ends;
   they hide when everything fits.
--------------------------------------------------------------------------- */
class AmorCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    if (!this.track) return;
    this.arrows = [...this.querySelectorAll('[data-carousel-step]')];
    this.arrows.forEach((arrow) =>
      arrow.addEventListener('click', () => {
        const step = Number(arrow.dataset.carouselStep) * this.track.clientWidth * 0.9;
        this.track.scrollBy({ left: step, behavior: window.AmorMotion.scroll() });
      })
    );
    this.sync = () => {
      const max = this.track.scrollWidth - this.track.clientWidth;
      this.classList.toggle('is-static', max <= 1);
      if (this.arrows[0]) this.arrows[0].disabled = this.track.scrollLeft <= 1;
      if (this.arrows[1]) this.arrows[1].disabled = this.track.scrollLeft >= max - 1;
    };
    this.track.addEventListener('scroll', this.sync, { passive: true });
    this.resizeObserver = new ResizeObserver(this.sync);
    this.resizeObserver.observe(this.track);
    this.sync();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }
}
customElements.define('amor-carousel', AmorCarousel);

/* ---------------------------------------------------------------------------
   <amor-header>: transparent-to-blurred on scroll, and the mega menu that
   opens on hover or keyboard focus, closes on leaving or Esc.
--------------------------------------------------------------------------- */
class AmorHeader extends HTMLElement {
  connectedCallback() {
    if (this.dataset.activeWorld) window.DemoStore?.setLastWorld(this.dataset.activeWorld);
    this.onScroll = () => this.classList.toggle('is-scrolled', window.scrollY > 40);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.onScroll();

    this.triggers = [...this.querySelectorAll('[data-mega-trigger]')];
    this.triggers.forEach((trigger) => {
      trigger.addEventListener('pointerenter', () => {
        if (desktopHover.matches) this.openMega(trigger);
      });
      trigger.addEventListener('focus', () => {
        if (!this.restoringFocus) this.openMega(trigger);
      });
    });
    this.querySelectorAll('[data-mega-close-on-hover]').forEach((element) =>
      element.addEventListener('pointerenter', () => this.closeMega())
    );
    this.addEventListener('pointerleave', () => this.closeMega());
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.closeMega();
    });
    // Focus on the logo, the search or an icon closes the open panel (full pass, stage א7):
    // otherwise its links came next in Tab order, before the page.
    this.addEventListener('focusin', (event) => {
      if (!this.openTrigger || event.target.matches('[data-mega-trigger]')) return;
      if (!this.panelFor(this.openTrigger)?.contains(event.target)) this.closeMega();
    });
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Tab') this.onTab(event);
      if (event.key !== 'Escape' || !this.openTrigger) return;
      const trigger = this.openTrigger;
      this.closeMega();
      this.restoringFocus = true;
      trigger.focus({ preventScroll: true });
      this.restoringFocus = false;
    });
  }

  /**
   * Tab on an open world goes into its panel; Tab after the panel's last link goes
   * on to the next world (or the logo after the last one); Shift+Tab from the
   * first link goes back to the world.
   */
  onTab(event) {
    const trigger = this.openTrigger;
    const panel = trigger && this.panelFor(trigger);
    if (!panel) return;
    const links = [...panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled])')].filter(
      (element) => element.getClientRects().length > 0
    );
    if (!links.length) return;
    let target = null;
    if (!event.shiftKey && event.target === trigger) target = links[0];
    else if (event.shiftKey && event.target === links[0]) target = trigger;
    else if (!event.shiftKey && event.target === links[links.length - 1]) {
      const next = this.triggers[this.triggers.indexOf(trigger) + 1];
      target = next || this.querySelector('.header__logo');
      this.closeMega();
    }
    if (!target) return;
    event.preventDefault();
    if (target === trigger) this.restoringFocus = true;
    target.focus();
    this.restoringFocus = false;
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
  }

  panelFor(trigger) {
    return document.getElementById(trigger.getAttribute('aria-controls'));
  }

  openMega(trigger) {
    if (this.openTrigger === trigger) return;
    this.closeMega();
    this.panelFor(trigger)?.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
    this.classList.add('is-solid');
    this.openTrigger = trigger;
  }

  closeMega() {
    if (!this.openTrigger) return;
    this.panelFor(this.openTrigger)?.classList.remove('is-open');
    this.openTrigger.setAttribute('aria-expanded', 'false');
    this.classList.remove('is-solid');
    this.openTrigger = null;
  }
}
customElements.define('amor-header', AmorHeader);

/* ---------------------------------------------------------------------------
   <amor-newsletter>: Shopify's customer form, sent in place. "Already
   registered" comes from this browser's memory (stage 0, check 5).
--------------------------------------------------------------------------- */
class AmorNewsletter extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.input = this.form.querySelector('input[type="email"]');
    this.message = this.querySelector('[data-newsletter-message]');
    this.form.noValidate = true;
    // Shopify's bot-protection script intercepts the form's submit event and posts
    // the form itself, even after our preventDefault. So the button stops being a
    // submit button, and the form is sent only from here.
    const button = this.form.querySelector('[type="submit"]');
    if (button) {
      button.type = 'button';
      button.addEventListener('click', (event) => this.submit(event));
    }
    this.input?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') this.submit(event);
    });
    this.form.addEventListener('submit', (event) => this.submit(event));
  }

  show(kind) {
    if (!this.message) return;
    this.message.textContent = kind ? this.dataset[`message${kind}`] || '' : '';
    this.message.hidden = !kind;
    this.input?.setAttribute('aria-invalid', String(kind === 'Invalid'));
  }

  done() {
    const done = this.querySelector('[data-newsletter-done]');
    this.form.hidden = true;
    this.show(null);
    if (done) {
      done.hidden = false;
      done.focus?.();
    }
  }

  async submit(event) {
    event.preventDefault();
    const email = this.input.value.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      this.show('Invalid');
      this.input.focus();
      return;
    }
    if (window.DemoStore?.newsletterHas(email)) {
      this.show('Exists');
      return;
    }
    if (this.busy) return;
    this.busy = true;
    this.show(null);
    try {
      const response = await fetch(this.form.action, { method: 'POST', body: new FormData(this.form) });
      if (response.ok && /[?&]customer_posted=true/.test(response.url)) {
        window.DemoStore?.newsletterAdd(email);
        this.done();
        return;
      }
      // A challenge page or anything unexpected: let Shopify take the normal route.
      window.DemoStore?.newsletterAdd(email);
      HTMLFormElement.prototype.submit.call(this.form);
    } catch {
      this.show('Error');
    } finally {
      this.busy = false;
    }
  }
}
customElements.define('amor-newsletter', AmorNewsletter);

/* ---------------------------------------------------------------------------
   <amor-collection>: the category page listing (spec chapter 13). Sorting,
   filtering and "load more" ask Shopify for the same section again (Section
   Rendering API) and swap its [data-collection-part] elements. The URL keeps
   the filters and the sort, so a filtered page can be shared. Coming back from
   a product restores the loaded pages and the scroll position from
   history.state (not browser storage). If a request fails, the browser simply
   goes to the address, so nothing is ever stuck half loaded.
--------------------------------------------------------------------------- */
const PRICE_PARAMS = ['filter.v.price.gte', 'filter.v.price.lte'];

class AmorCollection extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.pages = 1;
    this.addEventListener('click', (event) => this.onClick(event));
    this.addEventListener('change', (event) => this.onChange(event));
    this.addEventListener('drawer:open', () => this.onDrawerOpen());
    this.onPopState = async (event) => {
      await this.navigate(window.location.href, { push: false });
      this.restore(event.state?.amorCollection);
    };
    window.addEventListener('popstate', this.onPopState);
    this.onPageHide = () => this.saveState();
    window.addEventListener('pagehide', this.onPageHide);
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
    this.syncView();
    this.snapshotForm();
    this.restore(window.history.state?.amorCollection);
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.onPopState);
    window.removeEventListener('pagehide', this.onPageHide);
  }

  get form() {
    return this.querySelector('[data-filter-form]');
  }

  get grid() {
    return this.querySelector('[data-grid]');
  }

  onClick(event) {
    const view = event.target.closest('[data-view]');
    if (view) {
      window.DemoStore?.setGridView(view.dataset.view);
      this.syncView();
      return;
    }
    const more = event.target.closest('[data-load-more]');
    if (more) {
      event.preventDefault();
      this.loadMore(more);
      return;
    }
    const filterLink = event.target.closest('[data-filter-link]');
    if (filterLink) {
      event.preventDefault();
      this.navigate(filterLink.href);
      return;
    }
    if (event.target.closest('[data-filter-clear]')) {
      this.form?.querySelectorAll('input:checked').forEach((input) => (input.checked = false));
      this.preview();
      return;
    }
    if (event.target.closest('[data-filter-apply]')) {
      const url = this.urlFromForm();
      this.querySelector('amor-drawer')?.close();
      this.navigate(url);
      return;
    }
    if (event.target.closest('[data-grid] a')) this.saveState();
  }

  onChange(event) {
    const sort = event.target.closest('[data-sort]');
    if (sort) {
      const label = this.querySelector('[data-sort-value]');
      if (label) label.textContent = sort.selectedOptions[0]?.textContent.trim() || '';
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      if (sort.value === this.dataset.defaultSort) url.searchParams.delete('sort_by');
      else url.searchParams.set('sort_by', sort.value);
      this.navigate(url.toString());
      return;
    }
    if (!event.target.closest('[data-filter-form]')) return;
    // The four price pills are one choice: picking one clears the others.
    if (event.target.matches('[data-price-range]') && event.target.checked) {
      this.form
        .querySelectorAll('[data-price-range]')
        .forEach((input) => input !== event.target && (input.checked = false));
    }
    this.formDirty = true;
    this.preview();
  }

  syncView() {
    const view = window.DemoStore?.gridView() || 'default';
    this.querySelectorAll('[data-view]').forEach((button) =>
      button.setAttribute('aria-pressed', String(button.dataset.view === view))
    );
  }

  /** The address for the drawer's current choices, keeping the sort. */
  urlFromForm() {
    const params = new URLSearchParams();
    if (this.form) {
      for (const [name, value] of new FormData(this.form)) {
        if (name.startsWith('filter.')) params.append(name, value);
      }
      const price = this.form.querySelector('[data-price-range]:checked');
      if (price?.dataset.gte) params.set(PRICE_PARAMS[0], price.dataset.gte);
      if (price?.dataset.lte) params.set(PRICE_PARAMS[1], price.dataset.lte);
    }
    const sort = new URL(window.location.href).searchParams.get('sort_by');
    if (sort) params.set('sort_by', sort);
    const query = params.toString();
    return `${this.dataset.url}${query ? `?${query}` : ''}`;
  }

  async fetchSection(url, sectionId = this.sectionId) {
    const target = new URL(url, window.location.origin);
    target.searchParams.set('section_id', sectionId);
    const response = await fetch(target);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  }

  /** Replaces each named [data-collection-part] with the one in doc. */
  swapParts(doc, names) {
    names.forEach((name) => {
      const fresh = doc.querySelector(`[data-collection-part="${name}"]`);
      const current = document.querySelector(`[data-collection-part="${name}"]`);
      if (!fresh || !current) return;
      if (name === 'drawer-form') {
        // Keep the filter groups that were open.
        const open = [...current.querySelectorAll('details[open]')].map((d) => d.dataset.filterGroup);
        fresh.querySelectorAll('details[data-filter-group]').forEach((details) => {
          details.open = open.includes(details.dataset.filterGroup);
        });
      }
      current.replaceWith(document.importNode(fresh, true));
    });
  }

  snapshotForm() {
    this.applied = {
      form: this.form?.cloneNode(true),
      footer: this.querySelector('[data-collection-part="drawer-footer"]')?.cloneNode(true),
    };
    this.formDirty = false;
  }

  skeletonItems(count) {
    const template = this.querySelector('template[data-skeleton]');
    if (!template) return [];
    return Array.from({ length: count }, () => template.content.firstElementChild.cloneNode(true));
  }

  showLoading() {
    const results = this.querySelector('[data-collection-part="results"]');
    if (!results) return;
    results.setAttribute('aria-busy', 'true');
    const count = Math.min(Math.max(this.grid?.children.length || 0, 2), 6);
    let grid = this.grid;
    if (!grid) {
      results.querySelector('.collection-empty')?.remove();
      grid = document.createElement('ul');
      grid.className = 'product-grid product-grid--switchable';
      grid.dataset.grid = '';
      results.prepend(grid);
    }
    grid.replaceChildren(...this.skeletonItems(count));
  }

  announce() {
    const status = this.querySelector('[data-collection-status]');
    const count = document.querySelector('[data-collection-part="count"]');
    if (status && count) status.textContent = count.textContent.trim();
  }

  /** Shows the listing for url: filters, sort, a removed filter pill, or back/forward. */
  async navigate(url, { push = true } = {}) {
    const token = (this.navigateToken = (this.navigateToken || 0) + 1);
    this.showLoading();
    if (push) {
      // Keep the toolbar and the first results in view.
      const top = this.getBoundingClientRect().top + window.scrollY - (document.querySelector('.header')?.offsetHeight || 0);
      if (window.scrollY > top) window.scrollTo({ top });
    }
    try {
      const doc = await this.fetchSection(url);
      if (token !== this.navigateToken) return;
      this.swapParts(doc, ['count', 'filter-count', 'active', 'results', 'drawer-form', 'drawer-footer']);
      const sort = doc.querySelector('[data-sort]');
      if (sort) {
        this.querySelector('[data-sort]').value = sort.value;
        const label = this.querySelector('[data-sort-value]');
        if (label) label.textContent = doc.querySelector('[data-sort-value]')?.textContent || label.textContent;
      }
      this.pages = 1;
      if (push) window.history.pushState({ amorCollection: { pages: 1, y: 0 } }, '', url);
      this.snapshotForm();
      syncWishlist();
      this.announce();
    } catch {
      window.location.href = url;
    }
  }

  /** Live counts in the drawer while choosing: options, price pills and the "show" button. */
  async preview() {
    const token = (this.previewToken = (this.previewToken || 0) + 1);
    const url = this.urlFromForm();
    this.updatePriceCounts(url);
    let doc;
    try {
      doc = await this.fetchSection(url);
    } catch {
      return;
    }
    if (token !== this.previewToken || !this.form) return;
    doc.querySelectorAll('[data-filter-option]').forEach((fresh) => {
      const current = this.form.querySelector(`[data-filter-option="${CSS.escape(fresh.dataset.filterOption)}"]`);
      if (!current) return;
      const count = current.querySelector('[data-option-count]');
      if (count) count.textContent = fresh.querySelector('[data-option-count]')?.textContent || '';
      const input = current.querySelector('input');
      const empty = fresh.classList.contains('is-empty') && !input.checked;
      current.classList.toggle('is-empty', empty);
      if (input.type === 'checkbox' && input.getAttribute('role') !== 'switch') input.disabled = empty;
    });
    this.swapParts(doc, ['drawer-footer']);
  }

  /** Counts each price pill from the products that match every other filter. */
  async updatePriceCounts(url) {
    const options = this.querySelectorAll('[data-price-option]');
    if (!options.length) return;
    const token = (this.priceToken = (this.priceToken || 0) + 1);
    const target = new URL(url, window.location.origin);
    PRICE_PARAMS.forEach((param) => target.searchParams.delete(param));
    target.searchParams.delete('page');
    let prices;
    try {
      const doc = await this.fetchSection(target.toString(), 'collection-prices');
      prices = JSON.parse(doc.querySelector('[data-prices]')?.textContent || 'null');
    } catch {
      return;
    }
    if (token !== this.priceToken || !Array.isArray(prices)) return;
    this.querySelectorAll('[data-price-option]').forEach((option) => {
      const input = option.querySelector('[data-price-range]');
      const from = input.dataset.gte ? Number(input.dataset.gte) * 100 : -Infinity;
      const to = input.dataset.lte ? Number(input.dataset.lte) * 100 : Infinity;
      const count = prices.filter((price) => price >= from && price <= to).length;
      option.querySelector('[data-option-count]').textContent = String(count);
      const empty = count === 0 && !input.checked;
      option.classList.toggle('is-empty', empty);
      input.disabled = empty;
    });
  }

  onDrawerOpen() {
    // Choices that were never applied are dropped when the drawer opens again.
    if (this.formDirty && this.applied?.form) {
      this.form?.replaceWith(this.applied.form.cloneNode(true));
      this.querySelector('[data-collection-part="drawer-footer"]')?.replaceWith(this.applied.footer.cloneNode(true));
      this.formDirty = false;
    }
    this.updatePriceCounts(window.location.href);
  }

  async loadMore(link, { restoring = false } = {}) {
    if (this.loadingMore) return false;
    this.loadingMore = true;
    link.setAttribute('aria-disabled', 'true');
    const grid = this.grid;
    const skeletons = this.skeletonItems(4);
    grid?.append(...skeletons);
    try {
      const doc = await this.fetchSection(link.href);
      skeletons.forEach((item) => item.remove());
      const items = [...doc.querySelectorAll('[data-grid] > li')].map((item) => document.importNode(item, true));
      grid.append(...items);
      this.swapParts(doc, ['more']);
      this.pages += 1;
      syncWishlist();
      if (!restoring) {
        this.saveState();
        items[0]?.querySelector('.card__title a')?.focus({ preventScroll: true });
      }
      return true;
    } catch {
      window.location.href = link.href;
      return false;
    } finally {
      this.loadingMore = false;
    }
  }

  saveState() {
    const state = { ...(window.history.state || {}), amorCollection: { pages: this.pages, y: Math.round(window.scrollY) } };
    window.history.replaceState(state, '');
  }

  /** Back from a product: load the same pages again, then return to the same spot. */
  async restore(saved) {
    if (!saved) return;
    for (let page = 1; page < saved.pages; page += 1) {
      const link = this.querySelector('[data-load-more]');
      if (!link || !(await this.loadMore(link, { restoring: true }))) break;
    }
    window.scrollTo(0, saved.y || 0);
  }
}
customElements.define('amor-collection', AmorCollection);

/* ---------------------------------------------------------------------------
   Wishlist hearts (cards, header indicator) and the quick-add plus.
--------------------------------------------------------------------------- */
const syncWishlist = () => {
  if (!window.DemoStore) return;
  const ids = window.DemoStore.wishlist();
  document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
    const on = ids.includes(button.dataset.wishlistToggle);
    button.setAttribute('aria-pressed', String(on));
    const label = on ? button.dataset.labelRemove : button.dataset.labelAdd;
    if (label) button.setAttribute('aria-label', label);
  });
  document.querySelectorAll('[data-wishlist-indicator]').forEach((element) =>
    element.classList.toggle('is-filled', ids.length > 0)
  );
};

document.addEventListener('click', (event) => {
  const heart = event.target.closest('[data-wishlist-toggle]');
  if (heart) {
    event.preventDefault();
    window.DemoStore?.toggleWishlist(heart.dataset.wishlistToggle, heart.dataset.wishlistColor, heart.dataset.wishlistImage);
    return;
  }
  const plus = event.target.closest('[data-quick-add]');
  if (plus) {
    event.preventDefault();
    // Stage 2 listens for this and opens the quick-add drawer; until then, go to the product.
    const handled = !document.dispatchEvent(
      new CustomEvent('quick-add:open', {
        cancelable: true,
        detail: { handle: plus.dataset.quickAdd, variant: plus.dataset.quickAddVariant, trigger: plus },
      })
    );
    if (!handled) window.location.href = plus.dataset.fallbackUrl;
  }
});
document.addEventListener('demo-store:change', syncWishlist);

/* ---------------------------------------------------------------------------
   The AMOR BY YOU request field: an empty request is not sent; an example
   pill fills the field and sends it. Sending goes to /pages/amor-by-you?q=.
--------------------------------------------------------------------------- */
document.addEventListener('submit', (event) => {
  const form = event.target.closest('[data-request-form]');
  if (!form) return;
  const input = form.querySelector('input[name="q"]');
  const message = form.querySelector('[data-request-message]');
  // An attached image is a request too; the text is then optional (spec chapter 11).
  const hasImage = form.querySelector('[data-request-chip]:not([hidden])');
  if (input.value.trim() || hasImage) {
    if (message) message.hidden = true;
    return;
  }
  event.preventDefault();
  if (message) {
    message.textContent = form.dataset.messageEmpty;
    message.hidden = false;
  }
  input.focus();
});

document.addEventListener('click', (event) => {
  const example = event.target.closest('[data-request-example]');
  if (!example) return;
  const form = example.closest('.request')?.querySelector('[data-request-form]');
  if (!form) return;
  form.querySelector('input[name="q"]').value = example.textContent.trim();
  form.requestSubmit();
});

/* Designers index: the all / Women / Men pills show the brands of that world. */
document.addEventListener('click', (event) => {
  const pill = event.target.closest('[data-designer-world]');
  if (!pill) return;
  const world = pill.dataset.designerWorld;
  pill.closest('[data-designers]')?.querySelectorAll('[data-worlds]').forEach((item) => {
    item.hidden = world !== 'all' && !item.dataset.worlds.split(' ').includes(world);
  });
});

/* Single-choice pill groups: one pressed pill at a time (sizes, sub-categories). */
document.addEventListener('click', (event) => {
  const pill = event.target.closest('[data-pill-group] button.pill');
  if (!pill || pill.disabled) return;
  pill
    .closest('[data-pill-group]')
    .querySelectorAll('button.pill')
    .forEach((button) => button.setAttribute('aria-pressed', String(button === pill)));
});

/* ---------------------------------------------------------------------------
   Page-wide: no-connection notice, reveal on scroll.
--------------------------------------------------------------------------- */
const syncOnline = () => {
  const notice = document.querySelector('[data-offline-notice]');
  if (notice) notice.hidden = navigator.onLine;
};
window.addEventListener('online', syncOnline);
window.addEventListener('offline', syncOnline);

const revealObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) =>
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-revealed');
            revealObserver.unobserve(entry.target);
          }),
        { rootMargin: '0px 0px -10% 0px' }
      )
    : null;

const init = () => {
  syncWishlist();
  syncOnline();
  document.querySelectorAll('.reveal').forEach((element) => {
    if (revealObserver) revealObserver.observe(element);
    else element.classList.add('is-revealed');
  });
};

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
